import React, { useState, useEffect } from 'react';
import InboxPopover from './InboxPopover';
import {
  Box,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
  alpha,
  Drawer,
  AppBar,
  Toolbar,
  useMediaQuery,
  useTheme,
  Switch,
  ListItem,
  Dialog,
  Slide
} from '@mui/material';
import {
  Home as HomeIcon,
  DirectionsCar as CarIcon,
  People as PeopleIcon,
  ReceiptLong,
  ReceiptLong as SalesIcon,
  BarChart as ReportsIcon,
  Settings as SettingsIcon,
  HelpOutline as HelpIcon,
  Logout as LogoutIcon,
  Campaign as AdvertisingIcon,
  Assignment as TaskIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  KeyboardArrowDown as ArrowDownIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  Calculate as CalculateIcon,
  AttachMoney as AttachMoneyIcon,
  CalendarMonth as CalendarIcon,
  Construction as ToolsIcon,
  Psychology as IntelligenceIcon,
  Search as SearchIcon,
  AutoAwesome as AIIcon,
  Build as BuildIcon,
  CloudUpload as MigrationIcon,
  Inventory as StockIcon,
  BusinessCenter as OperationsIcon,
  AccountBalance as FinanceIcon,
  TrendingUp as MarketingIcon,
  AdminPanelSettings as SystemIcon,
  AccountBox as AccountIcon,
  RocketLaunch as GettingStartedIcon,
  Announcement as AnnouncementIcon,
  CreditCard as SubscriptionIcon,
  Description as LicenceIcon,
  ContactPhone as ContactIcon,
  Backup as BackupIcon,
  Add as AddIcon,
  Handshake as HandshakeIcon,
  MonetizationOn as MoneyIcon,
  ShoppingCart as PurchaseIcon,
  RequestQuote as QuoteIcon,
  PersonAdd as PersonAddIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  ViewSidebar as SidebarIcon,
  ViewDay as TopNavIcon
} from '@mui/icons-material';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useThemeMode } from '../../contexts/ThemeContext';
import { useVoiceMode } from '../../contexts/VoiceModeContext';
import LocationSelector from '../LocationSelector';
import { getCurrentPageTitle } from '../../config/navigation';
import { useFilteredNavigation } from '../../hooks/useFilteredNavigation';
import { NavigationProvider, AppBreadcrumbs, useNavigationSafe } from '../../contexts/NavigationContext';
import GlobalSearchModal from '../GlobalSearchModal';
import AppleStyleDealFlowEnhanced from '../AppleStyleDealFlowEnhanced';
import AddCustomerModal from '../customers/AddCustomerModal';
import QuickAddExpenseModal from '../QuickAddExpenseModal';
import MobileBottomNav from './MobileBottomNav';
import MobileFullscreenMenu from './MobileFullscreenMenu';
import ScrollToTop from '../ScrollToTop';
import ServiceFlowDialog from '../ServiceFlowDialog';

const SIDEBAR_WIDTH = 240;
const SIDEBAR_COLLAPSED_WIDTH = 90; // Wider for icons with labels

// Stable transition component for profile menu - prevents React from recreating it on every render
const ProfileSlideTransition = React.forwardRef(function Transition(
  props: any,
  ref: React.Ref<unknown>,
) {
  return <Slide direction="left" ref={ref} {...props} timeout={{ enter: 300, exit: 250 }} />;
});

export default function ResponsiveLayout() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));


  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileFullscreenMenuOpen, setMobileFullscreenMenuOpen] = useState(false);
  const [mobileProfileMenuOpen, setMobileProfileMenuOpen] = useState(false);
  const [desktopCollapsed, setDesktopCollapsed] = useState(
    isTablet || localStorage.getItem('sidebarDefault') === 'collapsed'
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [pendingThemeToggle, setPendingThemeToggle] = useState(false);
  const [quickAddAnchorEl, setQuickAddAnchorEl] = useState<null | HTMLElement>(null);
  const [quickExpenseOpen, setQuickExpenseOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    'Sales': false,
    'Vehicles': false,
    'Operations': false,
    'Account': false,
    'System': false
  });
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  // dealFlowOpen state removed — sale creation routes to /sales/new
  const [dealFlowMode, setDealFlowMode] = useState<'sale' | 'quote'>('quote');
  const [addCustomerModalOpen, setAddCustomerModalOpen] = useState(false);
  const [serviceFlowOpen, setServiceFlowOpen] = useState(false);
  // proposalFlowOpen / saleFlowOpen removed — Quick Add menu navigates to /sales/new?mode=...
  const [collapsedMenuAnchor, setCollapsedMenuAnchor] = useState<{el: HTMLElement | null, section: string}>({el: null, section: ''});

  const navigate = useNavigate();
  const location = useLocation();
  const isFullBleed = ['/ai', '/inbox'].includes(location.pathname);
  const { user, currentOrganization, signOut } = useAuth();
  const { mode: themeMode, toggleTheme } = useThemeMode();
  const { isVoiceModeActive } = useVoiceMode();
  const { createNavigationHandler } = useNavigationSafe();
  const [navigationLayout, setNavigationLayout] = useState(
    localStorage.getItem('navigationLayout') || 'side'
  );

  // CRITICAL UX FIX: Scroll to top on route change
  useEffect(() => {
    // Find the main scrollable container
    const mainContent = document.querySelector('main');
    if (mainContent) {
      mainContent.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
    }
    // Also scroll window to top (fallback)
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  }, [location.pathname]);

  // Global keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Search shortcut (Cmd+K or Ctrl+K)
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        setSearchModalOpen(true);
      }
      // Quick Add shortcut (Cmd+N or Ctrl+N)
      if ((event.metaKey || event.ctrlKey) && event.key === 'n') {
        event.preventDefault();
        // Toggle quick add menu - simulate button click
        const btn = document.getElementById('quick-add-button');
        if (btn) btn.click();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    console.log('🔵 PROFILE MENU OPENED');
    if (isMobile) {
      setMobileProfileMenuOpen(true);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleProfileMenuClose = () => {
    console.log('🔴 PROFILE MENU CLOSED');
    if (isMobile) {
      setMobileProfileMenuOpen(false);
    } else {
      setAnchorEl(null);
    }
  };

  const handleLogout = async () => {
    handleProfileMenuClose();
    await signOut();
    navigate('/login');
  };

  const handleLayoutToggle = () => {
    const newLayout = navigationLayout === 'side' ? 'top' : 'side';
    setNavigationLayout(newLayout);
    localStorage.setItem('navigationLayout', newLayout);
    // Dispatch event to trigger layout change
    window.dispatchEvent(new CustomEvent('navigationLayoutChange', { detail: { layout: newLayout } }));
  };

  const handleQuickAddMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setQuickAddAnchorEl(event.currentTarget);
  };

  const handleQuickAddMenuClose = () => {
    setQuickAddAnchorEl(null);
  };

  const handleQuickAddAction = (path: string) => {
    handleQuickAddMenuClose();

    // Special handling for tasks - navigate to calendar with dialog open
    if (path === '/tasks') {
      navigate('/calendar?openAdd=true');
      return;
    }

    // Special handling for customers - open AddCustomerModal
    if (path === '/customers/add') {
      setAddCustomerModalOpen(true);
      return;
    }

    // Quick-add Proposal → wizard route in quote mode
    if (path === '/quote') {
      navigate('/sales/new?mode=quote');
      return;
    }

    // Quick-add Sale → wizard route in sale mode
    if (path === '/sales/add') {
      navigate('/sales/new?mode=sale');
      return;
    }

    // TEMPORARILY HIDDEN: Workshop
    // Special handling for workshop - open service flow dialog
    // if (path === '/workshop') {
    //   setServiceFlowOpen(true);
    //   return;
    // }

    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  // Create navigation handler with middle-click support
  const handleNavigationClick = (path: string) => {
    const handler = createNavigationHandler(path);
    return (e: React.MouseEvent) => {
      // Close mobile drawer if open
      if (isMobile) {
        setMobileOpen(false);
      }
      // Call the navigation handler
      handler(e);
    };
  };

  const toggleSection = (section: string) => {
    // Close all other sections first, then toggle the clicked one
    setExpandedSections(prev => {
      const allClosed = Object.keys(prev).reduce((acc, key) => {
        acc[key] = false;
        return acc;
      }, {} as Record<string, boolean>);
      
      return {
        ...allClosed,
        [section]: !prev[section]
      };
    });
  };

  // Use permission-filtered menu structure
  const { filteredNavigation: menuStructure } = useFilteredNavigation();

  const sidebarWidth = desktopCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;

  const drawerContent = (
    <>
      {/* Mobile Header Only */}
      {isMobile && (
        <>
          <Box
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              minHeight: 64,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                '&:hover': { opacity: 0.8 }
              }}
              onClick={createNavigationHandler('/')}
              onMouseDown={createNavigationHandler('/')}
            >
              <img
                src="/DealerOS - Icon.svg"
                alt="DealerOS Icon"
                style={{
                  height: '36px',
                  width: '36px',
                  marginRight: '10px'
                }}
              />
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                Dealer<Box component="span" sx={{ fontWeight: 800 }}>OS</Box>
              </Typography>
            </Box>
            <IconButton onClick={handleDrawerToggle}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider />
        </>
      )}

      {/* Navigation */}
      <List sx={{ px: 1, py: isMobile ? 1 : 2, flexGrow: 1 }}>
        {menuStructure.map((item) => {
          // Standalone items
          if (item.standalone && item.path) {
            const isActive = location.pathname === item.path ||
              (item.path === '/' && location.pathname === '/');
            const isCollapsed = desktopCollapsed && !isMobile;

            if (isCollapsed) {
              // Collapsed view: icon with label underneath
              return (
                <Box key={item.text || item.path}>
                  <ListItemButton
                    onClick={handleNavigationClick(item.path)}
                    onMouseDown={handleNavigationClick(item.path)}
                    selected={isActive}
                    sx={{
                      mb: 0,
                      borderRadius: 1,
                      minHeight: 80,
                      px: 1,
                      py: 2,
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      transition: 'background-color 0.05s cubic-bezier(0.4, 0, 1, 1)',
                      '&.Mui-selected': {
                        bgcolor: alpha('#10b981', 0.1),
                        color: 'primary.main',
                        '& .MuiSvgIcon-root': {
                          color: 'primary.main',
                        },
                      },
                      '&:hover': {
                        bgcolor: alpha('#fff', 0.05),
                      },
                    }}
                  >
                    <Box sx={{ fontSize: '1.5rem', display: 'flex', mb: 0.75 }}>
                      {React.cloneElement(item.icon as React.ReactElement, { fontSize: 'inherit' })}
                    </Box>
                    {item.text && (
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '0.7rem',
                          fontWeight: isActive ? 600 : 500,
                          color: isActive ? 'primary.main' : 'text.secondary',
                          textAlign: 'center',
                          lineHeight: 1.2,
                        }}
                      >
                        {item.text}
                      </Typography>
                    )}
                  </ListItemButton>
                  <Divider sx={{ my: 0.5, opacity: 0.5 }} />
                </Box>
              );
            }

            // Regular expanded view
            return (
              <ListItemButton
                key={item.text || item.path}
                onClick={handleNavigationClick(item.path)}
                onMouseDown={handleNavigationClick(item.path)}
                selected={isActive}
                sx={{
                  mb: 0.5,
                  borderRadius: 1,
                  minHeight: 40,
                  px: 2,
                  justifyContent: 'flex-start',
                  transition: 'background-color 0.05s cubic-bezier(0.4, 0, 1, 1)',
                  '&.Mui-selected': {
                    bgcolor: alpha('#10b981', 0.1),
                    color: 'primary.main',
                    '& .MuiListItemIcon-root': {
                      color: 'primary.main',
                    },
                  },
                  '&:hover': {
                    bgcolor: alpha('#fff', 0.05),
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 40,
                    color: isActive ? 'primary.main' : 'text.secondary',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {item.text && (
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: isActive ? 600 : 500,
                    }}
                  />
                )}
              </ListItemButton>
            );
          }
          
          // Grouped sections
          if (item.children) {
            const isExpanded = expandedSections[item.text];
            const hasActiveChild = item.children.some(child =>
              location.pathname === child.path
            );
            const isCollapsed = desktopCollapsed && !isMobile;

            if (isCollapsed) {
              // Collapsed view: icon with label underneath, click opens popover
              return (
                <Box key={item.text}>
                  <ListItemButton
                    onClick={(event) => {
                      setCollapsedMenuAnchor({ el: event.currentTarget, section: item.text });
                    }}
                    sx={{
                      mb: 0,
                      borderRadius: 1,
                      minHeight: 80,
                      px: 1,
                      py: 2,
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      bgcolor: hasActiveChild ? alpha('#10b981', 0.05) : 'transparent',
                      transition: 'background-color 0.05s cubic-bezier(0.4, 0, 1, 1)',
                      '&:hover': {
                        bgcolor: alpha('#fff', 0.05),
                      },
                    }}
                  >
                    <Box sx={{
                      fontSize: '1.5rem',
                      display: 'flex',
                      mb: 0.75,
                      color: hasActiveChild ? 'primary.main' : 'text.secondary'
                    }}>
                      {React.cloneElement(item.icon as React.ReactElement, { fontSize: 'inherit' })}
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: '0.7rem',
                        fontWeight: hasActiveChild ? 700 : 600,
                        color: hasActiveChild ? 'primary.main' : 'text.secondary',
                        textAlign: 'center',
                        lineHeight: 1.2,
                      }}
                    >
                      {item.text}
                    </Typography>
                  </ListItemButton>
                  <Divider sx={{ my: 0.5, opacity: 0.5 }} />
                </Box>
              );
            }

            // Regular expanded view
            return (
              <Box key={item.text} sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => toggleSection(item.text)}
                  sx={{
                    borderRadius: 1,
                    minHeight: 40,
                    px: 2,
                    justifyContent: 'flex-start',
                    bgcolor: hasActiveChild ? alpha('#10b981', 0.05) : 'transparent',
                    transition: 'background-color 0.05s cubic-bezier(0.4, 0, 1, 1)',
                    '&:hover': {
                      bgcolor: alpha('#fff', 0.05),
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 40,
                      color: hasActiveChild ? 'primary.main' : 'text.secondary',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: hasActiveChild ? 700 : 600,
                    }}
                  />
                  {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </ListItemButton>

                {/* Child items */}
                {isExpanded && (
                  <List sx={{ pl: 2, transition: 'none' }}>
                    {item.children.map(child => {
                      const isActive = location.pathname === child.path;

                      return (
                        <ListItemButton
                          key={child.text}
                          onClick={handleNavigationClick(child.path)}
                          onMouseDown={handleNavigationClick(child.path)}
                          selected={isActive}
                          sx={{
                            borderRadius: 1,
                            minHeight: 36,
                            mb: 0.25,
                            px: 1.5,
                            transition: 'background-color 0.05s cubic-bezier(0.4, 0, 1, 1)',
                            '&.Mui-selected': {
                              bgcolor: alpha('#10b981', 0.1),
                              color: 'primary.main',
                              '& .MuiListItemIcon-root': {
                                color: 'primary.main',
                              },
                            },
                            '&:hover': {
                              bgcolor: alpha('#fff', 0.05),
                            },
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 32,
                              color: isActive ? 'primary.main' : 'text.secondary',
                            }}
                          >
                            {child.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={child.text}
                            primaryTypographyProps={{
                              fontSize: '0.813rem',
                              fontWeight: isActive ? 600 : 500,
                            }}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                )}
              </Box>
            );
          }
          
          return null;
        })}
      </List>
    </>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: 'background.default' }}>
        <ScrollToTop />

      {/* Desktop Top Bar - Full Width */}
      {!isMobile && (
          <Box
            sx={{
              height: 64,
              borderBottom: 1,
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              px: 2, // 16px padding
              py: 2, // 16px padding
              bgcolor: 'background.paper',
              gap: 2,
            }}
          >
            {/* Left Side - Collapse Button & Logo */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* Collapse/Expand Button - Aligned with sidebar */}
              <Box sx={{ ml: 1 }}>
                <IconButton
                  size="small"
                  onClick={() => {
                    const newCollapsed = !desktopCollapsed;
                    setDesktopCollapsed(newCollapsed);
                    localStorage.setItem('sidebarDefault', newCollapsed ? 'collapsed' : 'expanded');
                  }}
                  sx={{
                    color: 'text.primary',
                    border: 1,
                    borderColor: 'divider',
                    bgcolor: 'background.default',
                    transition: 'background-color 0.05s cubic-bezier(0.4, 0, 1, 1)',
                    '&:hover': {
                      bgcolor: alpha('#fff', 0.05),
                    },
                  }}
                >
                  {desktopCollapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
                </IconButton>
              </Box>

              {/* Logo */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  ml: 1,
                  '&:hover': { opacity: 0.8 }
                }}
                onClick={createNavigationHandler('/')}
                onMouseDown={createNavigationHandler('/')}
              >
                <img
                  src="/DealerOS - Icon.svg"
                  alt="DealerOS Icon"
                  style={{
                    height: '36px',
                    width: '36px',
                    marginRight: '8px'
                  }}
                />
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  Dealer<Box component="span" sx={{ fontWeight: 800 }}>OS</Box>
                </Typography>
              </Box>
            </Box>

            {/* Center - Wide Search Bar */}
            <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', maxWidth: 600, mx: 'auto' }}>
              <Box
                onClick={() => setSearchModalOpen(true)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 0.75,
                  borderRadius: 2,
                  border: 2,
                  borderColor: 'divider',
                  bgcolor: 'background.default',
                  cursor: 'pointer',
                  width: '100%',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: alpha('#10b981', 0.05),
                  },
                }}
              >
                <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: 'text.secondary', flexGrow: 1 }}>
                  Search...
                </Typography>
                <Typography variant="caption" sx={{
                  color: 'text.secondary',
                  bgcolor: alpha('#000', 0.05),
                  px: 0.75,
                  py: 0.25,
                  borderRadius: 0.5,
                  fontWeight: 500
                }}>
                  ⌘K
                </Typography>
              </Box>
            </Box>

            {/* Right Side Actions */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {/* Create Button with Text */}
              <Tooltip title="Quick Add (⌘N)" enterDelay={0} leaveDelay={0}>
                <Box
                  onClick={handleQuickAddMenuOpen}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 1,
                    bgcolor: alpha('#10b981', 0.1),
                    color: 'primary.main',
                    cursor: 'pointer',
                    transition: 'background-color 0.05s cubic-bezier(0.4, 0, 1, 1)',
                    '&:hover': {
                      bgcolor: alpha('#10b981', 0.2),
                    },
                  }}
                >
                  <AddIcon sx={{ fontSize: 20 }} />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Create
                  </Typography>
                </Box>
              </Tooltip>

              {/* Inbox popover — quick messages next to Create */}
              <InboxPopover />

              {/* TEMPORARILY HIDDEN: AI Assistant Icon */}
              {/* <Tooltip title="AI Assistant" enterDelay={0} leaveDelay={0}>
                <IconButton
                  onClick={() => navigate('/ai')}
                  sx={{
                    color: location.pathname === '/ai' ? 'primary.main' : 'text.primary',
                    transition: 'background-color 0.05s cubic-bezier(0.4, 0, 1, 1)',
                    '&:hover': {
                      bgcolor: theme.palette.mode === 'dark' ? alpha('#fff', 0.12) : alpha('#000', 0.08),
                    },
                  }}
                >
                  <AIIcon />
                </IconButton>
              </Tooltip> */}

              {/* TEMPORARILY HIDDEN: Intelligence Icon */}
              {/* <Tooltip title="Intelligence" enterDelay={0} leaveDelay={0}>
                <IconButton
                  onClick={() => navigate('/intelligence')}
                  sx={{
                    color: location.pathname === '/intelligence' ? 'primary.main' : 'text.primary',
                    transition: 'background-color 0.05s cubic-bezier(0.4, 0, 1, 1)',
                    '&:hover': {
                      bgcolor: theme.palette.mode === 'dark' ? alpha('#fff', 0.12) : alpha('#000', 0.08),
                    },
                  }}
                >
                  <IntelligenceIcon />
                </IconButton>
              </Tooltip> */}

              {/* Profile Icon */}
              <Tooltip title="Profile" enterDelay={0} leaveDelay={0}>
                <IconButton
                  onClick={handleProfileMenuOpen}
                  sx={{ p: 0.5 }}
                >
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      fontSize: '0.875rem',
                      bgcolor: 'primary.main',
                      color: 'background.default',
                    }}
                  >
                    {user?.email?.[0]?.toUpperCase()}
                  </Avatar>
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        )}

      {/* Content Area with Sidebar and Main - Below Top Nav */}
      <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
        {/* Sidebar - Desktop only (mobile uses bottom nav + fullscreen menu) */}
        {!isMobile && (
          <Box
            sx={{
              width: sidebarWidth,
              flexShrink: 0,
              bgcolor: 'background.paper',
              borderRight: 1,
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              transition: 'none',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {drawerContent}
          </Box>
        )}

        {/* Main Content Area */}
        <Box
          component="main"
          id="main-scroll-container"
          sx={{
            flexGrow: 1,
            bgcolor: 'background.default',
            display: 'flex',
            flexDirection: 'column',
            ...(isMobile ? {
              position: 'fixed',
              top: isVoiceModeActive ? 0 : 56,
              left: 0,
              right: 0,
              bottom: isVoiceModeActive ? 0 : 56,
              overflow: 'auto',
              WebkitOverflowScrolling: 'touch',
              transition: 'top 0.3s ease-in-out, bottom 0.3s ease-in-out',
            } : {
              overflow: 'auto',
            }),
          }}
        >
          <Box sx={{
            p: 0,
            px: isFullBleed ? 0 : { xs: 2, sm: 2, md: 3 },
            pb: isFullBleed ? 0 : { xs: 2, sm: 2, md: 3 },
            width: '100%',
            height: isFullBleed ? '100%' : 'auto',
            display: isFullBleed ? 'flex' : 'block',
            '& > *': {
              width: '100%',
              ...(isFullBleed && { height: '100%' })
            }
          }}>
            {/* Navigation Elements */}
            {location.pathname !== '/' && !isFullBleed && (
              <Box sx={{ pt: { xs: 2, sm: 2, md: 3 }, pb: 1 }}>
                <AppBreadcrumbs />
              </Box>
            )}
            <Outlet />
          </Box>
        </Box>
      </Box>

      {/* Profile Menu - Desktop */}
      {!isMobile && (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleProfileMenuClose}
          transitionDuration={0} // NO ANIMATION - Instant appearance (was 50ms)
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 200,
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              boxShadow: 'none',
            },
          }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {user?.email}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {currentOrganization?.name}
            </Typography>
          </Box>
          <Divider />

          {/* Theme Toggle */}
          <ListItem sx={{ px: 2, py: 1 }}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              {themeMode === 'dark' ? <DarkModeIcon fontSize="small" /> : <LightModeIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText
              primary="Dark Mode"
              primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}
            />
            <Switch
              edge="end"
              checked={themeMode === 'dark'}
              onChange={() => {
                toggleTheme();
                handleProfileMenuClose();
              }}
              size="small"
            />
          </ListItem>

          {/* Layout Toggle */}
          <ListItem sx={{ px: 2, py: 1 }}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              {navigationLayout === 'top' ? <TopNavIcon fontSize="small" /> : <SidebarIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText
              primary="Top Navigation"
              primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}
            />
            <Switch
              edge="end"
              checked={navigationLayout === 'top'}
              onChange={handleLayoutToggle}
              size="small"
            />
          </ListItem>

          <Divider />

          {/* Settings & Account Items */}
          <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/settings'); }} sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            General Settings
          </MenuItem>
          <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/admin/backups'); }} sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
            <ListItemIcon>
              <BackupIcon fontSize="small" />
            </ListItemIcon>
            Database Backups
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/subscription'); }} sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
            <ListItemIcon>
              <SubscriptionIcon fontSize="small" />
            </ListItemIcon>
            Subscription
          </MenuItem>
          <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/dealer-licences'); }} sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
            <ListItemIcon>
              <LicenceIcon fontSize="small" />
            </ListItemIcon>
            Dealer Licences
          </MenuItem>
          <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/bank-accounts'); }} sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
            <ListItemIcon>
              <FinanceIcon fontSize="small" />
            </ListItemIcon>
            Bank Accounts
          </MenuItem>
          <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/contacts'); }} sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
            <ListItemIcon>
              <ContactIcon fontSize="small" />
            </ListItemIcon>
            Contacts
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/help'); }} sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
            <ListItemIcon>
              <HelpIcon fontSize="small" />
            </ListItemIcon>
            Help & Support
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout} sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            Sign out
          </MenuItem>
        </Menu>
      )}

      {/* Profile Menu - Mobile Fullscreen */}
      {isMobile && (
        <Dialog
          fullScreen
          open={mobileProfileMenuOpen}
          onClose={handleProfileMenuClose}
          TransitionComponent={ProfileSlideTransition}
          TransitionProps={{
            onExited: () => {
              // After dialog is fully closed and unmounted, toggle theme if pending
              if (pendingThemeToggle) {
                setPendingThemeToggle(false);
                toggleTheme();
              }
            },
          }}
          disableScrollLock={true}
          disableEnforceFocus={true}
          disableAutoFocus={true}
          disableRestoreFocus={true}
          PaperProps={{
            sx: {
              bgcolor: 'background.default',
            }
          }}
        >
          {/* Header */}
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 1,
              bgcolor: 'background.paper',
              borderBottom: 1,
              borderColor: 'divider',
              px: 2,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="h6" fontWeight={600}>
              Profile
            </Typography>
            <IconButton onClick={handleProfileMenuClose} edge="end">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* User Info */}
          <Box
            sx={{
              bgcolor: 'background.paper',
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1.5,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Avatar
              sx={{
                width: 80,
                height: 80,
                bgcolor: 'primary.main',
                fontSize: '2rem',
              }}
            >
              {user?.email?.[0]?.toUpperCase()}
            </Avatar>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" fontWeight={600}>
                {user?.email}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {currentOrganization?.name}
              </Typography>
            </Box>
          </Box>

          {/* Menu List */}
          <List sx={{ flex: 1, overflow: 'auto', py: 2 }}>
            {/* Theme Toggle */}
            <ListItemButton
              onClick={() => {
                console.log('🌓 THEME TOGGLE CLICKED - Mobile');
                console.log('  Current theme:', themeMode);
                console.log('  Dialog open:', mobileProfileMenuOpen);
                console.log('  Body overflow:', document.body.style.overflow);
                console.log('  Body has mui-fixed class:', document.body.classList.contains('mui-fixed'));

                // Set flag and close dialog - theme will toggle after dialog fully closes
                setPendingThemeToggle(true);
                handleProfileMenuClose();
              }}
              sx={{ mx: 1, mb: 0.5, borderRadius: 1 }}
            >
              <ListItemIcon>
                {themeMode === 'dark' ? <DarkModeIcon /> : <LightModeIcon />}
              </ListItemIcon>
              <ListItemText primary="Dark Mode" />
              <Switch
                edge="end"
                checked={themeMode === 'dark'}
                onChange={(e) => {
                  console.log('🌓 THEME SWITCH TOGGLED');
                  setPendingThemeToggle(true);
                  handleProfileMenuClose();
                }}
                onClick={(e) => e.stopPropagation()}
              />
            </ListItemButton>

            <Divider sx={{ my: 1 }} />

            {/* Account Items */}
            <ListItemButton onClick={(e) => { handleProfileMenuClose(); handleNavigationClick('/settings')(e); }} onMouseDown={(e) => { if (e.button === 1 || e.ctrlKey || e.metaKey) handleProfileMenuClose(); handleNavigationClick('/settings')(e); }} sx={{ mx: 1, mb: 0.5, borderRadius: 1 }}>
              <ListItemIcon><SettingsIcon /></ListItemIcon>
              <ListItemText primary="General Settings" />
            </ListItemButton>

            <ListItemButton onClick={(e) => { handleProfileMenuClose(); handleNavigationClick('/admin/backups')(e); }} onMouseDown={(e) => { if (e.button === 1 || e.ctrlKey || e.metaKey) handleProfileMenuClose(); handleNavigationClick('/admin/backups')(e); }} sx={{ mx: 1, mb: 0.5, borderRadius: 1 }}>
              <ListItemIcon><BackupIcon /></ListItemIcon>
              <ListItemText primary="Database Backups" />
            </ListItemButton>

            <Divider sx={{ my: 1 }} />

            <ListItemButton onClick={(e) => { handleProfileMenuClose(); handleNavigationClick('/subscription')(e); }} onMouseDown={(e) => { if (e.button === 1 || e.ctrlKey || e.metaKey) handleProfileMenuClose(); handleNavigationClick('/subscription')(e); }} sx={{ mx: 1, mb: 0.5, borderRadius: 1 }}>
              <ListItemIcon><SubscriptionIcon /></ListItemIcon>
              <ListItemText primary="Subscription" />
            </ListItemButton>

            <ListItemButton onClick={(e) => { handleProfileMenuClose(); handleNavigationClick('/bank-accounts')(e); }} onMouseDown={(e) => { if (e.button === 1 || e.ctrlKey || e.metaKey) handleProfileMenuClose(); handleNavigationClick('/bank-accounts')(e); }} sx={{ mx: 1, mb: 0.5, borderRadius: 1 }}>
              <ListItemIcon><FinanceIcon /></ListItemIcon>
              <ListItemText primary="Bank Accounts" />
            </ListItemButton>

            <Divider sx={{ my: 1 }} />

            {/* Support Items */}
            <ListItemButton onClick={(e) => { handleProfileMenuClose(); handleNavigationClick('/help')(e); }} onMouseDown={(e) => { if (e.button === 1 || e.ctrlKey || e.metaKey) handleProfileMenuClose(); handleNavigationClick('/help')(e); }} sx={{ mx: 1, mb: 0.5, borderRadius: 1 }}>
              <ListItemIcon><HelpIcon /></ListItemIcon>
              <ListItemText primary="Help & Support" />
            </ListItemButton>

            <Divider sx={{ my: 1 }} />

            {/* Sign Out */}
            <ListItemButton onClick={handleLogout} sx={{ mx: 1, mb: 0.5, borderRadius: 1, color: 'error.main' }}>
              <ListItemIcon sx={{ color: 'error.main' }}><LogoutIcon /></ListItemIcon>
              <ListItemText primary="Sign out" />
            </ListItemButton>
          </List>
        </Dialog>
      )}

      {/* Global Search Modal */}
      <GlobalSearchModal open={searchModalOpen} onClose={() => setSearchModalOpen(false)} />

      {/* Quick Add Menu */}
      <Menu
        anchorEl={quickAddAnchorEl}
        open={Boolean(quickAddAnchorEl)}
        onClose={handleQuickAddMenuClose}
        transitionDuration={0}
        anchorReference={isMobile ? "none" : undefined}
        anchorOrigin={!isMobile ? {
          vertical: 'bottom',
          horizontal: 'center',
        } : undefined}
        transformOrigin={!isMobile ? {
          vertical: 'top',
          horizontal: 'center',
        } : undefined}
        slotProps={{
          paper: {
            sx: {
              ...(isMobile && {
                position: 'fixed',
                left: '50%',
                bottom: 72, // 56px nav + 16px spacing
                transform: 'translateX(-50%) !important',
                maxHeight: 'calc(100vh - 180px)',
              }),
              mt: isMobile ? 0 : 1,
              minWidth: 280,
              maxWidth: isMobile ? '90vw' : undefined,
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              boxShadow: isMobile ? '0 -4px 20px rgba(0, 0, 0, 0.15)' : 'none',
            },
          },
        }}
        PaperProps={{
          sx: {
            ...(isMobile && {
              position: 'fixed',
              left: '50%',
              bottom: 72, // 56px nav + 16px spacing
              transform: 'translateX(-50%) !important',
              maxHeight: 'calc(100vh - 180px)',
            }),
            mt: isMobile ? 0 : 1,
            minWidth: 280,
            maxWidth: isMobile ? '90vw' : undefined,
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            boxShadow: isMobile ? '0 -4px 20px rgba(0, 0, 0, 0.15)' : 'none',
          },
        }}
      >
        {/* Quick Actions */}
        <Box sx={{ px: 2, py: 1, bgcolor: alpha('#10b981', 0.05) }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main', textTransform: 'uppercase' }}>
            Quick Create
          </Typography>
        </Box>
        <MenuItem onClick={() => handleQuickAddAction('/inventory/add')} sx={{ py: 1.5 }}>
          <ListItemIcon>
            <CarIcon fontSize="small" sx={{ color: 'primary.main' }} />
          </ListItemIcon>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>Vehicle</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Add to stock</Typography>
          </Box>
        </MenuItem>
        <Divider sx={{ opacity: 0.5 }} />
        <MenuItem onClick={() => handleQuickAddAction('/tasks')} sx={{ py: 1.5 }}>
          <ListItemIcon>
            <TaskIcon fontSize="small" sx={{ color: 'primary.main' }} />
          </ListItemIcon>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>Task</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Create task & set reminder</Typography>
          </Box>
        </MenuItem>
        <Divider sx={{ opacity: 0.5 }} />
        <MenuItem onClick={() => handleQuickAddAction('/customers/add')} sx={{ py: 1.5 }}>
          <ListItemIcon>
            <PersonAddIcon fontSize="small" sx={{ color: 'primary.main' }} />
          </ListItemIcon>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>Contact</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Customer, supplier, finance co. or dealer</Typography>
          </Box>
        </MenuItem>
        <Divider sx={{ opacity: 0.5 }} />
        <MenuItem onClick={() => handleQuickAddAction('/quote')} sx={{ py: 1.5 }}>
          <ListItemIcon>
            <QuoteIcon fontSize="small" sx={{ color: 'primary.main' }} />
          </ListItemIcon>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>Proposal</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Send pricing to customer</Typography>
          </Box>
        </MenuItem>
        <Divider sx={{ opacity: 0.5 }} />
        <MenuItem onClick={() => handleQuickAddAction('/sales/add')} sx={{ py: 1.5 }}>
          <ListItemIcon>
            <HandshakeIcon fontSize="small" sx={{ color: 'primary.main' }} />
          </ListItemIcon>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>Sale</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Create a deal & close sale</Typography>
          </Box>
        </MenuItem>
        <Divider sx={{ opacity: 0.5 }} />
        {/* TEMPORARILY HIDDEN: Workshop */}
        {/* <MenuItem onClick={() => handleQuickAddAction('/workshop')} sx={{ py: 1.5 }}>
          <ListItemIcon>
            <ToolsIcon fontSize="small" sx={{ color: 'primary.main' }} />
          </ListItemIcon>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>New Workshop</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Service jobs & scheduling</Typography>
          </Box>
        </MenuItem>
        <Divider sx={{ opacity: 0.5 }} /> */}
        <MenuItem onClick={() => handleQuickAddAction('/purchase-orders/new')} sx={{ py: 1.5 }}>
          <ListItemIcon>
            <ReceiptLong fontSize="small" sx={{ color: 'primary.main' }} />
          </ListItemIcon>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>Purchase Order</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Order from supplier</Typography>
          </Box>
        </MenuItem>
        <Divider sx={{ opacity: 0.5 }} />
        <MenuItem onClick={() => { handleQuickAddMenuClose(); setQuickExpenseOpen(true); }} sx={{ py: 1.5 }}>
          <ListItemIcon>
            <MoneyIcon fontSize="small" sx={{ color: 'primary.main' }} />
          </ListItemIcon>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>Expense</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Vehicle or business expense</Typography>
          </Box>
        </MenuItem>
      </Menu>

      {/* Mobile Top Navigation */}
      {isMobile && !isVoiceModeActive && (
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            top: 0,
            bgcolor: 'background.paper',
            borderBottom: 1,
            borderColor: 'divider',
            zIndex: 1200,
            transition: 'transform 0.3s ease-in-out',
          }}
        >
          <Toolbar sx={{ justifyContent: 'space-between', px: 2, minHeight: '56px !important' }}>
            {/* Left: Hamburger + Logo */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                edge="start"
                onClick={() => setMobileFullscreenMenuOpen(true)}
                sx={{
                  color: 'text.primary',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                <MenuIcon />
              </IconButton>
              <Box
                onClick={createNavigationHandler('/')}
                onMouseDown={createNavigationHandler('/')}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  cursor: 'pointer',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    opacity: 0.8,
                  },
                  '&:active': {
                    transform: 'scale(0.97)',
                  },
                }}
              >
                <img
                  src="/DealerOS - Icon.svg"
                  alt="DealerOS Icon"
                  style={{
                    height: '28px',
                    width: '28px',
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: 'primary.main',
                    letterSpacing: '-0.5px',
                  }}
                >
                  DealerOS
                </Typography>
              </Box>
            </Box>

            {/* Right: Search + Profile */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                onClick={() => setSearchModalOpen(true)}
                sx={{
                  color: 'text.primary',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                <SearchIcon />
              </IconButton>
              <IconButton
                onClick={handleProfileMenuOpen}
                sx={{ p: 0.5 }}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    fontSize: '0.875rem',
                    bgcolor: 'primary.main',
                    color: 'background.default',
                  }}
                >
                  {user?.email?.[0]?.toUpperCase()}
                </Avatar>
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>
      )}

      {/* Mobile Bottom Navigation */}
      {isMobile && !isVoiceModeActive && (
        <MobileBottomNav
          onOpenQuickAdd={handleQuickAddMenuOpen}
        />
      )}

      {/* Mobile Fullscreen Menu */}
      {isMobile && (
        <MobileFullscreenMenu
          open={mobileFullscreenMenuOpen}
          onClose={() => setMobileFullscreenMenuOpen(false)}
        />
      )}

      {/* Add Customer Modal */}
      <AddCustomerModal
        open={addCustomerModalOpen}
        onClose={() => setAddCustomerModalOpen(false)}
        onSuccess={(customerId) => {
          navigate(`/customers/${customerId}`);
        }}
      />

      <QuickAddExpenseModal
        open={quickExpenseOpen}
        onClose={() => setQuickExpenseOpen(false)}
      />

      {/* Service Flow Dialog for Workshop */}
      <ServiceFlowDialog
        open={serviceFlowOpen}
        onClose={() => setServiceFlowOpen(false)}
        onSuccess={() => {
          setServiceFlowOpen(false);
          navigate('/workshop');
        }}
      />

      {/* Sale/Proposal modals removed — Quick Add menu navigates to /sales/new?mode=sale|quote */}

      {/* Collapsed Sidebar Popover Menu */}
      <Menu
        anchorEl={collapsedMenuAnchor.el}
        open={Boolean(collapsedMenuAnchor.el)}
        onClose={() => setCollapsedMenuAnchor({ el: null, section: '' })}
        transitionDuration={0}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            mt: 0,
            ml: 1,
            minWidth: 220,
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            boxShadow: theme.shadows[4],
          },
        }}
      >
        {collapsedMenuAnchor.section && menuStructure.find(m => m.text === collapsedMenuAnchor.section)?.children?.map((child, index, array) => {
          const isActive = location.pathname === child.path;
          return (
            <React.Fragment key={child.text}>
              <MenuItem
                onClick={(e) => {
                  const handler = handleNavigationClick(child.path);
                  handler(e);
                  setCollapsedMenuAnchor({ el: null, section: '' });
                }}
                onMouseDown={(e) => {
                  const handler = handleNavigationClick(child.path);
                  handler(e);
                  if (e.button === 1 || e.ctrlKey || e.metaKey) {
                    setCollapsedMenuAnchor({ el: null, section: '' });
                  }
                }}
                selected={isActive}
                sx={{
                  fontSize: '0.9rem',
                  fontWeight: isActive ? 600 : 500,
                  color: isActive ? 'primary.main' : 'inherit',
                  py: 1.5,
                  px: 2,
                  '&.Mui-selected': {
                    bgcolor: alpha('#10b981', 0.1),
                  },
                  '&:hover': {
                    bgcolor: alpha('#fff', 0.05),
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: isActive ? 'primary.main' : 'text.secondary' }}>
                  {child.icon}
                </ListItemIcon>
                {child.text}
              </MenuItem>
              {index < array.length - 1 && <Divider sx={{ opacity: 0.5 }} />}
            </React.Fragment>
          );
        })}
      </Menu>
    </Box>
  );
}